/**
 * CorrectiveActionsTab — staff log a non-conformance, admins/staff close
 * it once root cause + preventive measure are addressed. Open CAs are
 * surfaced with a badge counter on the parent tab trigger.
 */
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertTriangle } from "lucide-react";

export default function CorrectiveActionsTab({ cas, ccps, ca, setCa, canEdit, onSubmit, onClose }) {
  return (
    <>
      {canEdit && (
        <form onSubmit={onSubmit} className="surface p-6 mb-6 grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-ochre-600" />
            <h2 className="font-display font-bold text-lg">Log corrective action</h2>
          </div>
          <Input data-testid="ca-issue" placeholder="Issue / non-conformance" value={ca.issue}
            onChange={(e) => setCa({ ...ca, issue: e.target.value })} className="h-12" required />
          <Select value={ca.ccp_id} onValueChange={(v) => setCa({ ...ca, ccp_id: v })}>
            <SelectTrigger data-testid="ca-ccp" className="h-12">
              <SelectValue placeholder="Related CCP (optional)" />
            </SelectTrigger>
            <SelectContent>
              {ccps.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.step}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea data-testid="ca-action" rows={2} placeholder="Action taken" value={ca.action_taken}
            onChange={(e) => setCa({ ...ca, action_taken: e.target.value })} className="md:col-span-2" />
          <Input data-testid="ca-root" placeholder="Root cause" value={ca.root_cause}
            onChange={(e) => setCa({ ...ca, root_cause: e.target.value })} className="h-12" />
          <Input data-testid="ca-prevent" placeholder="Preventive measure" value={ca.preventive_measure}
            onChange={(e) => setCa({ ...ca, preventive_measure: e.target.value })} className="h-12" />
          <div className="md:col-span-2 flex justify-end">
            <Button type="submit" data-testid="ca-submit"
              className="h-12 bg-ochre-600 hover:bg-ochre-400 text-white">
              Log corrective action
            </Button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {cas.length === 0 && (
          <div className="surface p-10 text-center text-charcoal-soft">No corrective actions.</div>
        )}
        {cas.map((c) => (
          <div key={c.id} className={`surface p-5 ${c.closed ? "opacity-70" : ""}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {c.closed ? (
                    <Badge className="bg-eucalyptus-100 text-eucalyptus-900 hover:bg-eucalyptus-100">Closed</Badge>
                  ) : (
                    <Badge className="bg-ochre-600 text-white hover:bg-ochre-600">Open</Badge>
                  )}
                  <span className="text-xs font-mono text-charcoal-soft">
                    {new Date(c.created_at).toLocaleString("en-AU")} · {c.user_name}
                  </span>
                </div>
                <div className="font-medium mt-2">{c.issue}</div>
                {c.action_taken && (
                  <div className="text-sm mt-1"><b>Action:</b> {c.action_taken}</div>
                )}
                {c.root_cause && (
                  <div className="text-sm"><b>Root cause:</b> {c.root_cause}</div>
                )}
              </div>
              {canEdit && !c.closed && (
                <Button
                  data-testid={`ca-close-${c.id}`}
                  onClick={() => onClose(c.id)}
                  variant="outline"
                  className="h-10 gap-2 border-eucalyptus-700 text-eucalyptus-900"
                >
                  <CheckCircle2 className="w-4 h-4" /> Close
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
